import type { OnChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  Input,
} from '@angular/core';
import { trackById } from '@freelancer/ui/helpers';
import escapeStringRegexp from 'escape-string-regexp';
import type { TextSection } from './highlight-text.types';
import { HighlightBackground } from './highlight-text.types';

@Component({
  selector: 'fl-highlight-text',
  template: `
    <ng-container *ngFor="let section of textSections; trackBy: trackById">
      <span
        *ngIf="section.text"
        [class.Highlighted]="section.highlighted"
        [attr.data-background]="backgroundColor"
        >{{ section.text }}</span
      >
    </ng-container>
  `,
  styleUrls: ['./highlight-text.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HighlightTextComponent implements OnChanges {
  trackById = trackById;

  @Input() text: string;
  @Input() highlight: string;
  @Input() backgroundColor = HighlightBackground.LIGHT;

  @HostBinding('attr.data-truncate')
  @Input()
  truncate = false;

  textSections: TextSection[];

  private highlightTerms: string[];

  ngOnChanges(): void {
    if (!this.text || !this.highlight || this.highlight.trim() === '') {
      this.textSections = [{ text: this.text, highlighted: false }];
      return;
    }

    this.setHighlightTerms();

    const regexString = `(${this.highlightTerms
      .map(term => `\\b${term}`)
      .join('|')})`;
    const highlightRegex = new RegExp(regexString, 'i');

    this.textSections = this.text
      .split(highlightRegex)
      .map((extract: string, index: number) => ({
        id: index,
        text: extract,
        highlighted: !!extract.match(highlightRegex),
      }))
      .filter(section => section.text !== ''); // Remove empty sections
  }

  private setHighlightTerms(): void {
    const highlightTerm = escapeStringRegexp(this.highlight.trim());
    const individualHighlightTerms = highlightTerm.split(/\s+/);

    this.highlightTerms =
      individualHighlightTerms.length > 1
        ? [highlightTerm, ...individualHighlightTerms]
        : [highlightTerm];
  }
}
